// src/app/features/admin/walletApi.js
import http from "@app/api/http";

/**
 * Fetch treasury wallet balances
 * @returns {Promise<{balances: Array, totalUsdValue: number}>}
 */
export async function getTreasuryBalances() {
  try {
    console.log("Fetching treasury balances...");
    const res = await http.get("/admin/treasury/balances");
    console.log("Treasury balances response received");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch treasury balances:", error.response?.data || error.message);
    // Return fallback data structure to prevent UI errors
    return {
      balances: [],
      totalUsdValue: 0
    };
  }
}

/**
 * Fetch user balances with pagination
 * @param {Object} params - Query parameters (limit, offset, etc.)
 * @returns {Promise<{items: Array, total: number, limit: number, offset: number}>}
 */
export async function getUserBalances(params = {}) {
  try {
    console.log("Fetching user balances with params:", params);
    const res = await http.get("/admin/treasury/user-balances", { params });
    console.log("User balances response received");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch user balances:", error.response?.data || error.message);
    return {
      items: [],
      total: 0,
      limit: params.limit || 50,
      offset: params.offset || 0
    };
  }
}

/**
 * Fetch balance summary (treasury + users)
 * @returns {Promise<{balances: Array, totalUserValue: number, totalTreasuryValue: number}>}
 */
export async function getBalanceSummary() {
  try {
    console.log("Fetching balance summary...");
    const res = await http.get("/admin/treasury/balance-summary");
    console.log("Balance summary response received");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch balance summary:", error.response?.data || error.message);
    return {
      balances: [],
      totalUserValue: 0,
      totalTreasuryValue: 0
    };
  }
}

/**
 * Adjust user balance (add/subtract)
 * @param {Object} payload - { userId, currency, amount, reason }
 * @returns {Promise<Object>}
 */
export async function adjustUserBalance(payload) {
  try {
    console.log("Adjusting user balance:", payload);
    const res = await http.post("/admin/treasury/adjust-balance", payload);
    console.log("Adjust balance response received");
    return res.data;
  } catch (error) {
    console.error("Failed to adjust balance:", error.response?.data || error.message);
    throw error; // Re-throw to allow UI to show error message
  }
}

/**
 * Fetch pending withdrawal requests
 * @returns {Promise<{items: Array, total: number}>}
 */
export async function getPendingWithdrawals() {
  try {
    console.log("Fetching pending withdrawals...");
    const res = await http.get("/admin/treasury/withdrawals/pending");
    console.log("Pending withdrawals response received");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch pending withdrawals:", error.response?.data || error.message);
    return {
      items: [],
      total: 0
    };
  }
}

/**
 * Approve a withdrawal request
 * @param {string|number} withdrawalId - Withdrawal ID
 * @param {Object} opts - Additional options (txHash, notes, etc.)
 * @returns {Promise<Object>}
 */
export async function approveWithdrawal(withdrawalId, opts = {}) {
  try {
    console.log(`Approving withdrawal ${withdrawalId}:`, opts);
    const res = await http.post(`/admin/treasury/withdrawals/${withdrawalId}/approve`, opts);
    console.log("Withdrawal approval response received");
    return res.data;
  } catch (error) {
    console.error("Failed to approve withdrawal:", error.response?.data || error.message);
    throw error; // Re-throw to allow UI to show error message
  }
}

/**
 * Reject a withdrawal request
 * @param {string|number} withdrawalId - Withdrawal ID
 * @param {Object} opts - Additional options (reason, notes, etc.)
 * @returns {Promise<Object>}
 */
export async function rejectWithdrawal(withdrawalId, opts = {}) {
  try {
    console.log(`Rejecting withdrawal ${withdrawalId}:`, opts);
    const res = await http.post(`/admin/treasury/withdrawals/${withdrawalId}/reject`, opts);
    console.log("Withdrawal rejection response received");
    return res.data;
  } catch (error) {
    console.error("Failed to reject withdrawal:", error.response?.data || error.message);
    throw error; // Re-throw to allow UI to show error message
  }
}
